/**
 * Admin shell — responsive (A-* surface). Rendered under RequireRole("admin").
 *
 * - Desktop (lg+): fixed left sidebar + top bar.
 * - Mobile / tablet (< lg): sidebar hidden; nav becomes a fixed app-like bottom bar with
 *   icons. The top bar stays for branding + sign-out. Content gets bottom padding so the
 *   bottom bar never overlaps it.
 */

import type { ReactNode } from "react";

import { useAuth } from "../../auth/AuthContext";
import { AppShell, ShellSidebar, ShellTabBar, type ShellNavItem } from "../../components/AppShell";
import { Outlet } from "react-router-dom";

const NAV: ShellNavItem[] = [
  { to: "/staff/admin/medicines", label: "Medicines", short: "Meds", icon: <PillIcon /> },
  { to: "/staff/admin/doctors", label: "Doctors", short: "Doctors", icon: <StethIcon /> },
  { to: "/staff/admin/staff", label: "Staff", short: "Staff", icon: <UsersIcon /> },
  { to: "/staff/admin/orders", label: "Orders", short: "Orders", icon: <BoxIcon /> },
  { to: "/staff/admin/transactions", label: "Transactions", short: "Payments", icon: <LedgerIcon /> },
  { to: "/staff/admin/refunds", label: "Refunds", short: "Refunds", icon: <RefundIcon /> },
  { to: "/staff/admin/kpi", label: "KPIs", short: "KPIs", icon: <ChartIcon /> },
  { to: "/staff/admin/pharmacy-settings", label: "Pharmacy settings", short: "Settings", icon: <GearIcon /> },
  { to: "/staff/admin/status-master", label: "Status master", short: "Status", icon: <FlowIcon /> },
  { to: "/staff/admin/support", label: "Support", short: "Support", icon: <ChatIcon /> },
  { to: "/staff/admin/audit", label: "Audit", short: "Audit", icon: <ScrollIcon /> },
  { to: "/staff/admin/deletions", label: "Deletions", short: "Delete", icon: <TrashIcon /> },
];

export function AdminLayout() {
  const { user, logout } = useAuth();

  return (
    <AppShell
      title="Admin"
      userName={user?.full_name || "Admin"}
      onSignOut={() => void logout()}
      sidebar={<ShellSidebar nav={NAV} />}
      tabBar={<ShellTabBar nav={NAV} />}
    >
      <Outlet />
    </AppShell>
  );
}

/* --- inline icons (stroke=currentColor so they inherit active/inactive colour) --- */

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-full w-full">
      {children}
    </svg>
  );
}
function PillIcon() {
  return (
    <Svg>
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <path d="M12 8v8" />
    </Svg>
  );
}
function FlowIcon() {
  return (
    <Svg>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8.5 6H15a3 3 0 0 1 3 3v6.5" />
    </Svg>
  );
}
function GearIcon() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10 1.4 1.4m0-12.8-1.4 1.4m-10 10-1.4 1.4" />
    </Svg>
  );
}
function StethIcon() {
  return (
    <Svg>
      <path d="M6 3v5a4 4 0 0 0 8 0V3" />
      <path d="M10 14v2a4 4 0 0 0 8 0v-1" />
      <circle cx="18" cy="13" r="2" />
    </Svg>
  );
}
function UsersIcon() {
  return (
    <Svg>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19a6 6 0 0 1 12 0" />
      <path d="M16 6a3 3 0 0 1 0 6m5 7a5 5 0 0 0-4-5" />
    </Svg>
  );
}
function BoxIcon() {
  return (
    <Svg>
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5M12 13v8" />
    </Svg>
  );
}
function LedgerIcon() {
  return (
    <Svg>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18M7 15h4" />
    </Svg>
  );
}
function RefundIcon() {
  return (
    <Svg>
      <path d="M3 8h13a4 4 0 0 1 0 8H9" />
      <path d="M7 4 3 8l4 4" />
    </Svg>
  );
}
function ChartIcon() {
  return (
    <Svg>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </Svg>
  );
}
function ChatIcon() {
  return (
    <Svg>
      <path d="M4 5h16v11H8l-4 3V5Z" />
    </Svg>
  );
}
function ScrollIcon() {
  return (
    <Svg>
      <path d="M6 4h11v13a3 3 0 0 0 3 3H8a3 3 0 0 1-3-3V6a2 2 0 0 1 1-2Z" />
      <path d="M9 8h5M9 12h5" />
    </Svg>
  );
}
function TrashIcon() {
  return (
    <Svg>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
    </Svg>
  );
}
